const PLATFORM_LIMITS = {
  x: 280,
  linkedin: 3000,
  instagram: 2200,
  facebook: 63204,
};

const X_URL_LENGTH = 23;
const URL_REGEX = /(https?:\/\/[^\s]+)/gi;

export const normalizePlatformKey = (platformName = "") => {
  const normalized = platformName.toLowerCase().trim();
  if (normalized === "twitter/x" || normalized === "twitter / x" || normalized === "x" || normalized === "twitter") {
    return "x";
  }
  if (normalized === "linkedin") {
    return "linkedin";
  }
  if (normalized === "facebook") {
    return "facebook";
  }
  if (normalized === "instagram") {
    return "instagram";
  }
  return normalized;
};

export const formatPlatformErrorMessage = (rawMsg = "", platformName = "") => {
  if (!rawMsg) return `${platformName || "Platform"} action failed`;
  let msg = typeof rawMsg === "string" ? rawMsg.trim() : String(rawMsg);

  if (msg.startsWith("{") && msg.endsWith("}")) {
    try {
      const parsed = JSON.parse(msg);
      msg = parsed.detail || parsed.message || parsed.title || msg;
    } catch (ignored) {}
  }

  const pKey = normalizePlatformKey(platformName);
  const displayName = pKey === "x" ? "X" : (platformName || "the platform");

  if (msg.toLowerCase().includes("duplicate")) {
    return `Duplicate content is not allowed on ${displayName}, please update your content.`;
  }

  return msg;
};

const getCharacterCount = (value) => Array.from(value || "").length;

const getPlatformLength = (value, platformKey) => {
  const text = value || "";
  if (platformKey !== "x") {
    return getCharacterCount(text);
  }

  let total = 0;
  let lastIndex = 0;
  const matches = text.matchAll(URL_REGEX);
  for (const match of matches) {
    const matchedUrl = match[0];
    const matchIndex = match.index || 0;
    total += getCharacterCount(text.slice(lastIndex, matchIndex));
    total += X_URL_LENGTH;
    lastIndex = matchIndex + matchedUrl.length;
  }
  total += getCharacterCount(text.slice(lastIndex));
  return total;
};

export const getBusinessPageEntry = (businessPages = {}, platformName = "") =>
  businessPages?.[normalizePlatformKey(platformName)] || {};

export const getPlatformLimit = (platformName = "") =>
  PLATFORM_LIMITS[normalizePlatformKey(platformName)] || 0;

const capitalizeWord = (str) => {
  if (!str) return "";
  const replaced = str.replace(/[-_]/g, " ").trim();
  return replaced
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const extractNameFromUrl = (url) => {
  if (!url) return "";
  try {
    let cleanUrl = url.trim();
    if (cleanUrl.endsWith("/")) {
      cleanUrl = cleanUrl.slice(0, -1);
    }
    if (cleanUrl.includes("/company/")) {
      const index = cleanUrl.indexOf("/company/");
      let namePart = cleanUrl.substring(index + "/company/".length());
      if (namePart.includes("/")) {
        namePart = namePart.substring(0, namePart.indexOf("/"));
      }
      return capitalizeWord(namePart);
    }
    const parts = cleanUrl.split("/");
    if (parts.length > 0) {
      let lastPart = parts[parts.length - 1];
      if (lastPart.includes("?")) {
        lastPart = lastPart.substring(0, lastPart.indexOf("?"));
      }
      return capitalizeWord(lastPart);
    }
  } catch (ignored) {}
  return "";
};

export const isPayoneerDomain = (businessPages = {}) => {
  try {
    const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
    if (hostname.includes("payoneer") || hostname.includes("payoneers")) {
      return true;
    }
    const orgName = (businessPages?.orgName || "").toLowerCase();
    if (orgName.includes("payoneer")) {
      return true;
    }
    const storedSubdomain = (
      (typeof localStorage !== "undefined" && localStorage.getItem("organizationSubdomain")) ||
      (typeof sessionStorage !== "undefined" && sessionStorage.getItem("organizationSubdomain")) ||
      ""
    ).toLowerCase();
    if (storedSubdomain.includes("payoneer") || storedSubdomain.includes("payoneers")) {
      return true;
    }
  } catch (ignored) {}
  return false;
};

export const stripHashtagsFromText = (text = "") => {
  if (!text) return "";
  return text
    .replace(/(?:^|\s)#[A-Za-z0-9_äöüÄÖÜáéíóúÁÉÍÓÚ\u0400-\u04FF]+/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const getPlatformSuffix = (platformName = "", businessPages = {}) => {
  if (isPayoneerDomain(businessPages)) {
    return {
      mode: "none",
      text: "",
    };
  }

  const businessPage = getBusinessPageEntry(businessPages, platformName);
  if (businessPage?.tagText) {
    return {
      mode: "tag",
      text: businessPage.tagText,
    };
  }

  const platformKey = normalizePlatformKey(platformName);
  const username = businessPage?.username || "";
  const pageUrl = businessPage?.url || "";
  const displayName = businessPage?.displayName || businessPage?.name || "";
  const orgName = businessPages?.orgName || "";

  let attributionName = displayName ? displayName.trim() : (orgName ? orgName.trim() : "");
  if (!attributionName && pageUrl) {
    attributionName = extractNameFromUrl(pageUrl);
  }

  let text = "";
  if (platformKey === "x") {
    let handle = username ? username.replace(/^@/, "").trim() : "";
    if (!handle && pageUrl) {
      const match = pageUrl.match(/(?:x\.com|twitter\.com)\/([a-zA-Z0-9_]+)/i);
      if (match && !["home", "explore", "messages", "notifications", "search", "i"].includes(match[1].toLowerCase())) {
        handle = match[1];
      }
    }
    text = handle ? `@${handle}` : "";
  } else if (platformKey === "instagram") {
    text = username ? `@${username}` : attributionName;
  } else if (platformKey === "facebook") {
    text = username ? `@${username}` : (attributionName ? `Follow Us: ${attributionName}` : "");
  } else if (platformKey === "linkedin") {
    text = attributionName ? `Follow Us: ${attributionName}` : "";
  } else {
    text = attributionName;
  }

  return {
    mode: text ? "tag" : "none",
    text: text,
  };
};

const normalizeUrl = (url) => {
  if (!url) return "";
  let normalized = url.trim().toLowerCase();
  normalized = normalized.replace(/^https?:\/\//, "");
  normalized = normalized.replace(/^www\./, "");
  
  const queryIdx = normalized.indexOf('?');
  const hashIdx = normalized.indexOf('#');
  let endPathIdx = normalized.length;
  if (queryIdx !== -1 && hashIdx !== -1) {
    endPathIdx = Math.min(queryIdx, hashIdx);
  } else if (queryIdx !== -1) {
    endPathIdx = queryIdx;
  } else if (hashIdx !== -1) {
    endPathIdx = hashIdx;
  }
  
  let path = normalized.substring(0, endPathIdx);
  const suffix = normalized.substring(endPathIdx);
  if (path.endsWith("/")) {
    path = path.slice(0, -1);
  }
  return path + suffix;
};

const areUrlsEquivalent = (url1, url2) => {
  if (!url1 || !url2) return false;
  const norm1 = normalizeUrl(url1);
  const norm2 = normalizeUrl(url2);
  return norm1 === norm2 && norm1 !== "";
};

export const deduplicateContentLinks = (content) => {
  if (!content) return "";
  const matches = Array.from(content.matchAll(URL_REGEX));
  const seenNormalized = new Set();
  let sb = "";
  let lastEnd = 0;
  for (const match of matches) {
    const fullUrl = match[0];
    const norm = normalizeUrl(fullUrl);
    const start = match.index;
    const end = match.index + fullUrl.length;

    sb += content.slice(lastEnd, start);
    if (!seenNormalized.has(norm)) {
      seenNormalized.add(norm);
      sb += fullUrl;
    }
    lastEnd = end;
  }
  sb += content.slice(lastEnd);
  
  return sb.replace(/^[ \t]+$/gm, "").replace(/\n{3,}/g, "\n\n").trim();
};

export const containsSuffix = (content, suffixText) => {
  if (!content || !suffixText) return false;
  const lowerContent = content.toLowerCase();
  const lowerSuffix = suffixText.toLowerCase().trim();
  if (lowerContent.includes(lowerSuffix)) {
    return true;
  }

  const suffixUrls = suffixText.match(URL_REGEX);
  if (suffixUrls) {
    const contentUrls = content.match(URL_REGEX);
    if (contentUrls) {
      for (const sUrl of suffixUrls) {
        for (const cUrl of contentUrls) {
          if (areUrlsEquivalent(cUrl, sUrl)) {
            return true;
          }
        }
      }
    }
  }

  const handleRegex = /(@[\w_]+)/g;
  const suffixHandles = suffixText.match(handleRegex);
  if (suffixHandles) {
    const contentHandles = content.match(handleRegex);
    if (contentHandles) {
      for (const sHandle of suffixHandles) {
        for (const cHandle of contentHandles) {
          if (cHandle.toLowerCase() === sHandle.toLowerCase()) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

export const containsHashtag = (content, hashtag) => {
  if (!content || !hashtag) return false;
  let tag = hashtag.trim();
  if (tag.startsWith("#")) {
    tag = tag.substring(1);
  }
  if (!tag) return false;
  const escapedTag = tag.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  const regex = new RegExp(`(?:^|\\s)#${escapedTag}(?:$|\\s|[.,\\/#!$%\\^&\\*;:{}=\\-_~\(\)])`, "i");
  return regex.test(content);
};

export const appendHashtags = (content, hashtags, businessPages = {}) => {
  if (isPayoneerDomain(businessPages)) {
    return content || "";
  }
  if (!hashtags || hashtags.length === 0) {
    return content;
  }
  const normalizedContent = (content || "").trim();
  let currentContent = normalizedContent;

  const uniqueTags = new Set();
  for (const tag of hashtags) {
    if (tag && typeof tag === "string" && tag.trim()) {
      let cleanTag = tag.trim();
      if (!cleanTag.startsWith("#")) {
        cleanTag = "#" + cleanTag;
      }
      uniqueTags.add(cleanTag);
    }
  }

  let appendedTags = "";
  for (const tag of uniqueTags) {
    if (!containsHashtag(currentContent, tag) && !containsHashtag(appendedTags, tag)) {
      if (appendedTags.length > 0) {
        appendedTags += " ";
      }
      appendedTags += tag;
    }
  }

  if (appendedTags.length > 0) {
    if (currentContent.length > 0) {
      currentContent += "\n\n";
    }
    currentContent += appendedTags;
  }
  return currentContent;
};

export const composePlatformContent = (
  content = "",
  platformName = "",
  businessPages = {},
  hashtags = [],
  fbLink = ""
) => {
  const isPayoneer = isPayoneerDomain(businessPages);
  let baseContent = deduplicateContentLinks(content || "");

  if (isPayoneer && baseContent) {
    baseContent = baseContent
      .replace(/(?:^|\s)#[A-Za-z0-9_äöüÄÖÜáéíóúÁÉÍÓÚ\u0400-\u04FF]+/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  const effectiveHashtags = isPayoneer ? [] : hashtags;
  const platformKey = normalizePlatformKey(platformName);
  const suffix = getPlatformSuffix(platformName, businessPages);

  let finalText = baseContent;
  if (suffix.text && !containsSuffix(finalText, suffix.text)) {
    if (finalText.length > 0) {
      finalText += "\n\n";
    }
    finalText += suffix.text.trim();
  }

  finalText = appendHashtags(finalText, effectiveHashtags, businessPages);

  if (fbLink) {
    finalText = deduplicateContentLinks(finalText);
  }
  finalText = deduplicateContentLinks(finalText);

  const limit = getPlatformLimit(platformName);
  const baseLength = getPlatformLength(baseContent, platformKey);
  const reservedSections = [];
  if (suffix.text) {
    reservedSections.push(suffix.text);
  }
  const hashtagText = (Array.isArray(hashtags) ? hashtags : [])
    .filter((tag) => typeof tag === "string" && tag.trim())
    .map((tag) => tag.trim().startsWith("#") ? tag.trim() : "#" + tag.trim())
    .join(" ");
  if (hashtagText && !containsHashtag(baseContent, hashtagText)) {
    reservedSections.push(hashtagText);
  }
  const reservedText = reservedSections.join("\n\n");
  const reservedLength = reservedText
    ? getPlatformLength(`${baseContent ? "\n\n" : ""}${reservedText}`, platformKey)
    : 0;

  const maxBaseCharacters = limit ? Math.max(limit - reservedLength, 0) : 0;
  const effectiveLength = getPlatformLength(finalText, platformKey);

  return {
    platformKey,
    limit,
    suffixMode: suffix.mode,
    suffixText: suffix.text,
    finalText,
    baseLength,
    reservedLength,
    effectiveLength,
    maxBaseCharacters,
    remainingCharacters: Math.max(maxBaseCharacters - baseLength, 0),
    isOverLimit: limit ? effectiveLength > limit : false,
    isSuffixTooLong: suffix.text ? reservedLength > limit : false,
  };
};

export const clampPlatformContent = (
  nextValue = "",
  platformName = "",
  businessPages = {},
  hashtags = []
) => {
  const composition = composePlatformContent(nextValue, platformName, businessPages, hashtags);
  if (!composition.limit || composition.baseLength <= composition.maxBaseCharacters) {
    return nextValue;
  }

  const characters = Array.from(nextValue);
  while (characters.length > 0) {
    characters.pop();
    const candidate = characters.join("");
    const candidateComposition = composePlatformContent(candidate, platformName, businessPages, hashtags);
    if (candidateComposition.baseLength <= candidateComposition.maxBaseCharacters) {
      return candidate;
    }
  }

  return "";
};
